<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1100">
    <v-card>
      <v-card-title class="dialog-title">
        {{ dialogTitle }}
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0 drilldown-body">
        <!-- KPI mini-cards -->
        <div v-if="summary" class="drilldown-kpi-cards">
          <div class="drilldown-kpi-card">
            <v-icon small color="#3b82f6" class="mr-1">mdi-ev-station</v-icon>
            <div class="drilldown-kpi-content">
              <span class="drilldown-kpi-value">{{ summary.count || 0 }}</span>
              <span class="drilldown-kpi-label">{{ $t('org-dashboard.sim-drilldown-count') }}</span>
            </div>
          </div>
          <div class="drilldown-kpi-card">
            <v-icon small color="#0EA5E9" class="mr-1">mdi-database</v-icon>
            <div class="drilldown-kpi-content">
              <span class="drilldown-kpi-value">{{ formattedTotalData }}</span>
              <span class="drilldown-kpi-label">{{ $t('org-dashboard.sim-drilldown-total-data') }}</span>
              <span v-if="summary.avgDataMB != null" class="drilldown-kpi-sub">{{ summary.avgDataMB }} MB / {{ $t('org-dashboard.sim-drilldown-avg-short') }}</span>
            </div>
          </div>
          <div v-if="summary.ctdTotalDataMB != null" class="drilldown-kpi-card">
            <v-icon small color="#6366F1" class="mr-1">mdi-progress-clock</v-icon>
            <div class="drilldown-kpi-content">
              <span class="drilldown-kpi-value">{{ formattedCtdData }}</span>
              <span class="drilldown-kpi-label">{{ $t('org-dashboard.sim-drilldown-ctd') }}</span>
              <span v-if="summary.ctdAvgDataMB != null" class="drilldown-kpi-sub">{{ summary.ctdAvgDataMB }} MB / {{ $t('org-dashboard.sim-drilldown-avg-short') }}</span>
            </div>
          </div>
        </div>

        <!-- Type tabs (hidden for distribution) -->
        <v-tabs v-if="simType !== 'distribution'" v-model="activeTab" background-color="transparent" class="drilldown-tabs" @change="onTabChange">
          <v-tab v-for="tab in tabs" :key="tab.key" class="drilldown-tab">
            <span class="drilldown-tab-dot" :style="{ backgroundColor: tab.color }"></span>
            {{ tab.label }}
          </v-tab>
        </v-tabs>

        <!-- Distribution label when coming from chart -->
        <div v-if="simType === 'distribution' && bucket" class="distribution-label">
          <v-icon small color="#6366F1" class="mr-1">mdi-chart-bar</v-icon>
          {{ $t('org-dashboard.sim-drilldown-distribution') }}: {{ bucket }}
        </div>

        <!-- Data table -->
        <div class="drilldown-table-wrap">
          <v-data-table
            :headers="tableHeaders"
            :items="items"
            :server-items-length="totalItems"
            :options.sync="internalOptions"
            :loading="loading"
            :no-data-text="$t('org-dashboard.health-no-data')"
            :footer-props="{
              'items-per-page-options': [10, 20, 50],
            }"
            @update:options="onOptionsChange"
            class="drilldown-table"
          >
            <template v-slot:item.identifier="{ item }">
              <a class="drilldown-link" @click.stop="$emit('charger-click', item.chargingPointId || item.id)">{{ item.identifier || item.name || '–' }}</a>
            </template>

            <template v-slot:item.locationName="{ item }">
              {{ item.locationName || item.location || '–' }}
            </template>

            <template v-slot:item.iccid="{ item }">
              <span class="iccid-mono">{{ item.iccid || '–' }}</span>
            </template>

            <template v-slot:item.simStatus="{ item }">
              <v-chip x-small :color="simStatusColor(item.simStatus)" text-color="white">
                {{ item.simStatus || '–' }}
              </v-chip>
            </template>

            <template v-slot:item.dataMB="{ item }">
              {{ item.dataMB != null ? item.dataMB + ' MB' : '–' }}
            </template>

            <template v-slot:item.ctdDataMB="{ item }">
              {{ item.ctdDataMB != null ? item.ctdDataMB + ' MB' : '–' }}
            </template>

            <template v-slot:item.lastConnection="{ item }">
              {{ formatDate(item.lastConnection) }}
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
const TABS = [
  { key: 'connected',    color: '#22c55e' },
  { key: 'disconnected', color: '#f59e0b' },
  { key: 'notInTele2',   color: '#ef4444' },
  { key: 'noSim',        color: '#9ca3af' },
]

export default {
  name: 'DashboardSimDrilldown',
  props: {
    value: { type: Boolean, default: false },
    simType: { type: String, default: 'connected' },
    bucket: { type: String, default: null },
    summary: { type: Object, default: null },
    items: { type: Array, default: () => [] },
    totalItems: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
  },

  data() {
    return {
      activeTab: this.typeToTabIndex(this.simType),
      internalOptions: { page: 1, itemsPerPage: 20 },
    }
  },

  computed: {
    tabs() {
      return TABS.map(t => ({
        ...t,
        label: this.$t('org-dashboard.sim-drilldown-' + t.key.replace(/([A-Z])/g, '-$1').toLowerCase()),
      }))
    },

    dialogTitle() {
      if (this.simType === 'distribution' && this.bucket) {
        return this.$t('org-dashboard.sim-drilldown-title') + ' — ' + this.$t('org-dashboard.sim-drilldown-distribution') + ' ' + this.bucket
      }
      const tab = TABS.find(t => t.key === this.simType)
      if (tab) {
        return this.$t('org-dashboard.sim-drilldown-title') + ' — ' + this.$t('org-dashboard.sim-drilldown-' + tab.key.replace(/([A-Z])/g, '-$1').toLowerCase())
      }
      return this.$t('org-dashboard.sim-drilldown-title')
    },

    showIccid() {
      return this.simType !== 'noSim'
    },

    formattedTotalData() {
      if (!this.summary) return '–'
      const mb = this.summary.totalDataMB || 0
      if (mb >= 1000) return (mb / 1024).toFixed(1) + ' GB'
      return mb + ' MB'
    },

    formattedCtdData() {
      if (!this.summary || this.summary.ctdTotalDataMB == null) return '–'
      const mb = this.summary.ctdTotalDataMB
      if (mb >= 1000) return (mb / 1024).toFixed(1) + ' GB'
      return mb + ' MB'
    },

    tableHeaders() {
      const headers = [
        { text: this.$t('org-dashboard.health-identifier'), value: 'identifier' },
        { text: this.$t('org-dashboard.fw-drilldown-location'), value: 'locationName' },
      ]
      if (this.showIccid) {
        headers.push({ text: 'ICCID', value: 'iccid', sortable: false })
      }
      headers.push(
        { text: this.$t('org-dashboard.sim-drilldown-sim-status'), value: 'simStatus', sortable: false },
        { text: this.$t('org-dashboard.sim-drilldown-data-mb'), value: 'dataMB' },
        { text: this.$t('org-dashboard.sim-drilldown-ctd-mb'), value: 'ctdDataMB' },
        { text: this.$t('org-dashboard.sim-drilldown-sessions'), value: 'sessions' },
        { text: this.$t('org-dashboard.fw-drilldown-last-connection'), value: 'lastConnection' },
      )
      return headers
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.activeTab = this.typeToTabIndex(this.simType)
      }
    },
    simType(val) {
      if (val !== 'distribution') {
        this.activeTab = this.typeToTabIndex(val)
      }
    },
  },

  methods: {
    typeToTabIndex(type) {
      const idx = TABS.findIndex(t => t.key === type)
      return idx >= 0 ? idx : 0
    },

    onTabChange(tabIndex) {
      const tab = TABS[tabIndex]
      if (tab && tab.key !== this.simType) {
        this.$emit('update:sim-type', tab.key)
      }
    },

    onOptionsChange(options) {
      this.internalOptions = options
      this.$emit('update:options', options)
    },

    simStatusColor(status) {
      const map = {
        connected: '#22c55e',
        CONNECTED: '#22c55e',
        disconnected: '#f59e0b',
        DISCONNECTED: '#f59e0b',
        notInTele2: '#ef4444',
        NOT_IN_TELE2: '#ef4444',
        noSim: '#9ca3af',
        NO_SIM: '#9ca3af',
      }
      return map[status] || '#9ca3af'
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(this.$i18n.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
}

.drilldown-body {
  height: 70vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.drilldown-kpi-cards {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  background: #f9fafb;
  flex-shrink: 0;
}

.drilldown-kpi-card {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  flex: 1;
  min-width: 0;
}

.drilldown-kpi-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.drilldown-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.drilldown-kpi-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drilldown-kpi-unit {
  font-size: 11px;
  font-weight: 400;
  color: #6b7280;
}

.drilldown-kpi-sub {
  font-size: 9px;
  font-weight: 500;
  color: #9ca3af;
  margin-top: 1px;
}

.drilldown-tabs {
  flex-shrink: 0;
  flex-grow: 0;

  ::v-deep .v-tabs-bar {
    background: #f9fafb !important;
    border-bottom: 1px solid #e5e7eb;
  }

  ::v-deep .v-tab {
    text-transform: none;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    min-width: 100px;
  }

  ::v-deep .v-tab--active {
    font-weight: 600;
  }
}

.drilldown-tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
}

.distribution-label {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.drilldown-table-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ::v-deep .drilldown-table.v-data-table {
    flex: 1;
    min-height: 0;
    display: flex !important;
    flex-direction: column !important;
  }

  ::v-deep .v-data-table__wrapper {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  ::v-deep thead th {
    position: sticky !important;
    top: 0;
    z-index: 1;
    background: #f9fafb !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    color: #6b7280 !important;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    border-bottom: 1px solid #e5e7eb !important;
    padding: 8px 10px !important;

    span {
      font-size: 11px !important;
      font-weight: 600 !important;
    }
  }

  ::v-deep tbody td {
    font-size: 12px !important;
    color: #374151;
    border-bottom: 1px solid #f3f4f6 !important;
    padding: 6px 10px !important;
  }

  ::v-deep tbody tr:hover {
    background: #f9fafb !important;
  }

  ::v-deep tbody tr:last-child td {
    border-bottom: none !important;
  }

  ::v-deep .v-data-table__empty-wrapper {
    vertical-align: top;

    td {
      padding: 48px 16px !important;
      color: #9ca3af;
      font-size: 13px;
    }
  }

  ::v-deep .v-data-footer {
    flex-shrink: 0;
    border-top: 1px solid #e5e7eb;
    font-size: 12px;
  }
}

.drilldown-link {
  color: #1976d2;
  text-decoration: none;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
    color: #1565c0;
  }
}

.iccid-mono {
  font-family: monospace;
  font-size: 11px;
  color: #6b7280;
}
</style>
